import { AtRiskDataType } from '@components/ListCard/interface'
import { SessionInfoList } from '@interfaces/LessonGroups'
import { DateRanges, StringRange } from '@berlitzplatforms/micro.ui.scheduling-calendar/lib'
import moment from 'moment'
import first from 'lodash/first'
import last from 'lodash/last'
import { MINS_PER_UNIT } from '@utils/constants'
import { flatten } from 'lodash'

declare interface OverAllLessonsType {
  TotalLessons: number
  LessonsTaken: number
}

export const getAtRiskData = (sessions: SessionInfoList[], lessonsRemaining: number): AtRiskDataType[] => {
  return sessions
    .filter((session) => session.AtRisk)
    .map((session) => ({
      isAtRisk: true,
      atRiskStudent: (session?.StudentName).split(' ').shift() || '',
      lessonsLeft: lessonsRemaining,
    }))
}

export const getDateRangeGroupings = (
  dates: DateRanges = [],
  withGaps: boolean = false,
  hoverRange: number = 15,
  HoursInARow?: number,
  MinimumBreakLength?: number
) => {
  const dateRangeGroupings = dates
    .sort(
      (a: StringRange, b: StringRange) =>
        parseInt(moment(a[1]).format('x'), 10) - parseInt(moment(b[1]).format('x'), 10)
    )
    .reduce((acc, curr) => {
      let newDates = [...acc, [curr]]

      // Group back to back datetime based on prev's end time and current start time
      if (acc.length && acc.some((res = []) => res.some((r) => r[1] === curr[0]))) {
        newDates = acc.map((res) => (res.some((r) => r[1] === curr[0]) ? [...res, curr] : res))
      }

      return newDates
    }, [] as DateRanges[])

  if (withGaps && HoursInARow && MinimumBreakLength) {
    return dateRangeGroupings.reduce((acc: any[], curr: any[]) => {
      let newDates = [...acc, curr]

      if (acc.length) {
        // Check gaps
        const prevEndTime = last(last(last(acc)))
        const currStartTime = first(first(curr))
        const gapDuration = moment.duration(moment(currStartTime!).diff(prevEndTime!)).asMinutes()
        const prevStartTime = first(first(last(acc)))
        const currEndTime = last(last(curr))
        const totalPrevNextDuration = moment.duration(moment(currEndTime!).diff(prevStartTime!)).asHours()

        if (
          (gapDuration === hoverRange && totalPrevNextDuration >= HoursInARow) ||
          (gapDuration > hoverRange && hoverRange !== MinimumBreakLength && MinimumBreakLength * 2 === gapDuration)
        ) {
          newDates = [...acc, [[prevEndTime, currStartTime], ...curr]]
        }
      }
      return newDates
    }, [])
  }

  return dateRangeGroupings
}

export const getHoursInARowRestrictions = (
  dates: DateRanges = [],
  HoursInARow: number = 0,
  MinimumBreakLength: number = MINS_PER_UNIT,
  UnitsPerLesson: number = 0,
  LessonsPerSession: number = 0
) =>
  flatten(
    getDateRangeGroupings(dates).map((res): StringRange[] | any[] => {
      // get the earliest, late dates, and the hours length of the back-to-back sesions
      const start = (first(res) || [])[0] || ''
      const end = (last(res) || [])[1] || ''
      const duration = moment.duration(moment(end).diff(start)).asHours()
      // get hours per sesison
      const totalLessonHours = Math.round(((UnitsPerLesson * LessonsPerSession * MINS_PER_UNIT) / 60) * 100) / 100
      const diff = HoursInARow - duration

      return duration >= HoursInARow || totalLessonHours > diff
        ? [
            [moment(start).clone().subtract(MinimumBreakLength, 'minutes').format(), start],
            [end, moment(end).clone().add(MinimumBreakLength, 'minutes').format()],
          ]
        : [[]]
    })
  ).filter((d) => d.length)

export const getSelectedIntervals = (selected: DateRanges) =>
  flatten(
    selected.map((select) => {
      let start = moment(select?.[0])
      const end = moment(select?.[1])
      let intervals: any = []

      while (start < end) {
        let startEnd = [start.toISOString()]
        start = start.add(15, 'minutes')
        startEnd = startEnd.concat([start.toISOString()])
        intervals = [...intervals, startEnd]
      }

      return intervals
    })
  )
